import { useMemo } from 'react'
import { DateTime } from 'luxon'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'
import {
  formatDate,
  matchFilterArrayIncludes,
  matchFilterBetweenDates,
  matchFilterNumber,
  matchFilterKey,
  matchFilterString,
} from '@domain-group/javascript-utils'

import type { EntityOptions } from '@redux/modules/types'

import useCurrentUser from '@hooks/useCurrentUser'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { TaskListModel, TaskListItemModel, TeamMemberModel } from '@domain-group/realhub-backend-api-types'

import { getPriorityWeighting } from '@functions/status'

const dateNow = DateTime.now().toFormat('yyyy-MM-dd')

const watchEntityKeys = ['taskListItems', 'taskListSections', 'teams']

const getAssigneeTeamIds = (params: { assigneeId: number, teamMembers: TeamMemberModel[] }) => {
  const { assigneeId, teamMembers } = params

  const teamIds = Object.values(teamMembers).filter((teamMember: TeamMemberModel) => {
    const { user_id } = teamMember
    return assigneeId === user_id
  }).map((teamMember: TeamMemberModel) => teamMember.team_id)

  return teamIds
}

interface useTaskListItemsOptions {
  performHttpRequests?: boolean,
  filters: {
    assignee_id?: 'all' | 'current_user' | number,
    brand_id?: number,
    completed?: boolean,
    end_completed_date?: string,
    end_created_date?: string,
    end_due_date?: string,
    limit?: number,
    overdue: boolean,
    parent_task_id?: number | string,
    priority?: string,
    start_completed_date?: string,
    start_created_date?: string,
    start_due_date?: string,
    status_id?: number,
    string?: string,
    sub_tasks?: boolean,
    task_list_id?: number,
    task_list_section_id?: number,
    task_type?: string,
    team_id?: number,
    template_project?: boolean,
    template_page_id?: number,
  },
  requestOptions?: EntityOptions,
}

export type useTaskListItemsPayload = {
  callbacks: {
    loadMore: () => void,
  },
  canLoadMore: boolean,
  filteredTaskListItems: TaskListItemModel[],
  filteredTaskListItemsCount: number,
  hasFilteredTaskListItems: boolean,
  loading: boolean,
}

function useTaskListItems(options: useTaskListItemsOptions): useTaskListItemsPayload {
  const { filters, requestOptions } = options || {}
  const {
    assignee_id: filterAssigneeId,
    brand_id: filterBrandId,
    completed: filterCompleted,
    end_completed_date: filterEndCompletedDate,
    end_created_date: filterEndCreatedDate,
    end_due_date: filterEndDueDate,
    overdue: filterOverdue,
    parent_task_id: filterParentTaskId,
    priority: filterPriority,
    start_completed_date: filterStartCompletedDate,
    start_created_date: filterStartCreatedDate,
    start_due_date: filterStartDueDate,
    status_id: filterStatusId,
    string: filterString,
    sub_tasks: filterSubTasks,
    task_list_id: filterTaskListId,
    task_list_section_id: filterTaskListSectionId,
    task_type: filterTaskType,
    team_id: filterTeamId,
    template_project: filterTemplateProject,
    template_page_id: filterTemplatePageId,
  } = filters

  const {
    updatedEntities: {
      taskListItems: taskListItemsUpdatedAt,
      taskListSections: taskListSectionsUpdatedAt,
      teams: teamsUpdatedAt,
    },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { taskLists, taskListItems, teamMembers } = useSelector(reduxState => reduxState.entities)

  const { currentUser } = useCurrentUser()

  const filteredTaskListItems = useMemo((): TaskListItemModel[] => {
    const filtered = Object.values(taskListItems).filter((taskListItem: TaskListItemModel) => {
      const {
        assignee_id,
        completed,
        dates,
        id,
        parent_task_id,
        priority,
        status_id,
        task_list_id,
        task_list_section_id,
        task_type,
        title,
        template_page_ids,
      } = taskListItem

      const taskList = task_list_id && taskLists[task_list_id] ? taskLists[task_list_id] : {} as TaskListModel
      const {
        owner_type, owner_id, template_project,
      } = taskList

      // @ts-expect-error TS2322
      const teamIds = filterTeamId ? getAssigneeTeamIds({ assigneeId: assignee_id, teamMembers }) : []

      const dueAt = formatDate(dates?.due?.date_time, 'dd/MM/yyyy HH:mm')
      // @ts-expect-error TS2339
      const createdAt = formatDate(dates?.created?.date_time, 'dd/MM/yyyy HH:mm')
      // @ts-expect-error TS2339
      const completedAt = formatDate(dates?.complete?.date_time, 'dd/MM/yyyy HH:mm')
      const matchId = id?.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const adjustedFilterAssigneeId = filterAssigneeId === 'current_user' ? currentUser.id : filterAssigneeId
      const matchAssignee = filterAssigneeId !== 'all' ? matchFilterNumber(assignee_id, adjustedFilterAssigneeId) : true

      const matchBrand = (owner_type ? matchFilterKey(owner_type, 'Brand') : true)
        && matchFilterNumber(owner_id, filterBrandId)
      const matchCompleted = typeof filterCompleted === 'boolean' ? completed === filterCompleted : true
      const matchCompletedDates = matchFilterBetweenDates(completedAt, filterStartCompletedDate, filterEndCompletedDate)
      const matchCreatedDates = matchFilterBetweenDates(createdAt, filterStartCreatedDate, filterEndCreatedDate)
      const matchDueDates = matchFilterBetweenDates(dueAt, filterStartDueDate, filterEndDueDate)
      const matchOverdue = filterOverdue ? matchFilterBetweenDates(dueAt, null, dateNow) : true
      const matchParentTask = matchFilterNumber(
        parent_task_id,
        // @ts-expect-error TS2345
        filterParentTaskId === 'none' ? undefined : filterParentTaskId,
      )
      const matchPriority = matchFilterKey(priority, filterPriority)
      const matchStatus = matchFilterNumber(status_id, filterStatusId)
      const matchSubTasks = filterSubTasks ? !!parent_task_id : !parent_task_id
      const matchTaskList = matchFilterNumber(task_list_id, filterTaskListId)
      const matchTaskListSection = matchFilterNumber(task_list_section_id, filterTaskListSectionId)
      // @ts-expect-error TS2345
      const matchTaskType = matchFilterKey(task_type, filterTaskType)
      const matchTeamId = matchFilterArrayIncludes(teamIds, filterTeamId)
      // @ts-expect-error TS2345
      const matchTemplateProject = matchFilterKey(template_project, filterTemplateProject)
      const matchTemplatePage = template_page_ids ? matchFilterArrayIncludes(
        template_page_ids,
        filterTemplatePageId ? Number(filterTemplatePageId) : undefined,
      ) : true

      return (
        matchAssignee
        && matchBrand
        && matchCompleted
        && matchCompletedDates
        && matchCreatedDates
        && matchDueDates
        && matchOverdue
        && matchParentTask
        && matchPriority
        && matchStatus
        && matchString
        && matchSubTasks
        && matchTaskList
        && matchTaskListSection
        && matchTaskType
        && matchTeamId
        && matchTemplateProject
        && matchTemplatePage
      )
    })

    // Sort by date, then priority
    const sorted = filtered.sort((a, b) => {
      // @ts-expect-error TS2538
      const itemA = filterSubTasks ? taskListItems[a.parent_task_id] : a
      const dueDateA = itemA?.dates?.due?.integer
      const priorityWeightingA = getPriorityWeighting(itemA?.priority)

      // @ts-expect-error TS2538
      const itemB = filterSubTasks ? taskListItems[b.parent_task_id] : b
      const dueDateB = itemB?.dates?.due?.integer
      const priorityWeightingB = getPriorityWeighting(itemB?.priority)

      // Same Due Date, Sort by Priority
      if (dueDateA === dueDateB){
        return priorityWeightingA > priorityWeightingB ? -1 : 1
      }

      // No Due Dates
      if (dueDateA === 0) return 1
      if (dueDateB === 0) return -1

      // Compare Due Dates
      if (dueDateA < dueDateB) return -1
      if (dueDateA > dueDateB) return 1

      return 0
    })

    return sorted
  }, [taskListItemsUpdatedAt, taskListSectionsUpdatedAt, teamsUpdatedAt, JSON.stringify(filters)])

  const filteredTaskListItemsCount = filteredTaskListItems.length

  const loadMorePayload = useLoadMore({
    loadedCount: filteredTaskListItemsCount,
    filters,
    limit: filters?.limit,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  useReduxAction(
    'taskListItems',
    'loadTaskListItems',
    {
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: () => performHttpRequests,
    },
  )

  const { loading } = useSelector(reduxState => reduxState.taskListItems)

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredTaskListItems,
    filteredTaskListItemsCount,
    hasFilteredTaskListItems: !!filteredTaskListItems.length,
    loading,
  }
}

export default useTaskListItems
